import {
  ArrowUpRight,
  BellRing,
  Bookmark,
  Camera,
  CheckCircle2,
  Clock,
  Inbox as InboxIcon,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import { Spinner } from '../components/ui/spinner';
import { type InboxCategory, type InboxItem, useInbox } from '../lib/api/inbox';
import { fmtRelative } from '../lib/date';
import { cn } from '../lib/utils';

/**
 * Editorial Inbox — single-screen aggregator of "what needs my attention
 * right now." Polls /admin/inbox every 30s; the topbar badge in
 * AppShell.tsx reads from the same hook so the count is always consistent.
 *
 * Each category is its own card. Empty categories collapse to a small
 * confirmation row ("Eyewitness queue is empty"); when ALL categories are
 * empty, the page shows a single celebration empty state instead of a
 * stack of empty cards.
 */
export default function InboxPage(): JSX.Element {
  const inbox = useInbox();

  const allEmpty = inbox.data?.totalCount === 0;

  return (
    <>
      <Topbar title="Inbox" />
      <PageHeader
        title="Inbox"
        eyebrow="Editorial"
        subtitle={
          inbox.data
            ? inbox.data.totalCount === 0
              ? 'Nothing waiting on you right now'
              : `${inbox.data.totalCount.toLocaleString()} item${
                  inbox.data.totalCount === 1 ? '' : 's'
                } need attention`
            : 'Loading…'
        }
      />

      <div className="space-y-6 px-6 pb-10">
        {inbox.isLoading ? (
          <Spinner label="Loading inbox…" />
        ) : allEmpty ? (
          <Empty
            icon={CheckCircle2}
            title="All caught up"
            description="No pending reports, in-flight pushes, building stories, or expiring board items. Check back later — this page polls every 30 seconds."
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <CategoryCard
              icon={Camera}
              accent="text-brand"
              title="Eyewitness queue"
              description="Citizen reports awaiting moderation."
              category={inbox.data?.eyewitness}
              emptyMessage="No reports waiting."
              renderItem={renderEyewitnessItem}
              footerHref="/eyewitness"
              footerLabel="Open queue"
            />

            <CategoryCard
              icon={BellRing}
              accent="text-warning"
              title="In-flight broadcasts"
              description="Breaking-news pushes inside the retraction window."
              category={inbox.data?.pendingPushes}
              emptyMessage="Nothing queued."
              renderItem={renderPendingPushItem}
              footerHref="/breaking?tab=history"
              footerLabel="Open history"
            />

            <CategoryCard
              icon={TrendingUp}
              accent="text-info"
              title="Stories with momentum"
              description="Upstream clusters running across multiple outlets in the last 6 hours — competitors are on it; consider writing or broadcasting."
              category={inbox.data?.storiesWithMomentum}
              emptyMessage="Nothing trending across upstream feeds."
              renderItem={renderStoryClusterItem}
              footerHref="/stories"
              footerLabel="Open stories"
            />

            <CategoryCard
              icon={Clock}
              accent="text-warning"
              title="News board expiring"
              description="Promoted items dropping off the board within the next 24 hours."
              category={inbox.data?.expiringBoard}
              emptyMessage="Nothing rolling off in the next 24 hours."
              renderItem={renderExpiringBoardItem}
              footerHref="/news-desk"
              footerLabel="Open news desk"
            />

            <CategoryCard
              icon={Bookmark}
              accent="text-brand"
              title="Flagged news-desk items"
              description="Upstream items you flagged for follow-up but haven't actioned."
              category={inbox.data?.flaggedNewsDesk}
              emptyMessage="No outstanding flags."
              renderItem={renderFlaggedItem}
              footerHref="/news-desk"
              footerLabel="Open news desk"
            />
          </div>
        )}
      </div>
    </>
  );
}

interface CategoryCardProps {
  icon: typeof InboxIcon;
  accent: string;
  title: string;
  description: string;
  category: InboxCategory | undefined;
  emptyMessage: string;
  renderItem: (item: InboxItem) => JSX.Element;
  footerHref?: string;
  footerLabel?: string;
}

function CategoryCard({
  icon: Icon,
  accent,
  title,
  description,
  category,
  emptyMessage,
  renderItem,
  footerHref,
  footerLabel,
}: CategoryCardProps): JSX.Element {
  const isEmpty = !category || category.count === 0;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', accent)} />
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <CardTitle>{title}</CardTitle>
              {category && category.count > 0 ? (
                <Badge variant="brand" className="tabular-nums">
                  {category.count}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-[12.5px] leading-snug text-ink-muted">{description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isEmpty ? (
          <p className="px-5 py-6 text-sm text-ink-muted">{emptyMessage}</p>
        ) : (
          <ul className="divide-y divide-line-hair">
            {category!.items.map(renderItem)}
          </ul>
        )}
        {!isEmpty && footerHref && footerLabel && category!.count > category!.items.length ? (
          <div className="border-t border-line bg-surface-tinted/40 px-5 py-2.5 text-right text-[12px]">
            <Link
              to={footerHref}
              className="inline-flex items-center gap-1 text-ink-muted hover:text-brand"
            >
              {footerLabel}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ─── Per-category item renderers ────────────────────────────────────

function renderEyewitnessItem(item: InboxItem): JSX.Element {
  return (
    <li key={item.id}>
      <Link to={item.href ?? '#'} className="group block px-5 py-3 hover:bg-surface-tinted">
        <p className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
          {item.title}
        </p>
        <p className="mt-1 flex items-center gap-2 text-[11px] text-ink-subtle">
          {item.subtitle ? <span className="truncate">{item.subtitle}</span> : null}
          <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
          <span className="shrink-0">{fmtRelative(item.occurredAt)}</span>
        </p>
      </Link>
    </li>
  );
}

function renderPendingPushItem(item: InboxItem): JSX.Element {
  const meta = (item.meta ?? {}) as { manual?: boolean; triggerSource?: string | null };
  return (
    <li key={item.id}>
      <Link
        to={item.href ?? '#'}
        className="group flex items-start gap-3 px-5 py-3 hover:bg-surface-tinted"
      >
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
            {item.title}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-ink-subtle">
            {item.subtitle ? <span className="truncate">{item.subtitle}</span> : null}
            <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
            <span className="shrink-0">fires {fmtRelative(item.occurredAt)}</span>
          </p>
        </div>
        <Badge variant={meta.manual ? 'brand' : 'neutral'} className="shrink-0">
          {meta.manual ? 'Manual' : 'Auto'}
        </Badge>
      </Link>
    </li>
  );
}

function renderStoryClusterItem(item: InboxItem): JSX.Element {
  const meta = (item.meta ?? {}) as { sources?: string[]; itemCount?: number };
  return (
    <li key={item.id}>
      <Link
        to={item.href ?? '#'}
        className="group flex items-start gap-3 px-5 py-3 hover:bg-surface-tinted"
      >
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
            {item.title}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-ink-subtle">
            <span className="shrink-0 font-mono tabular-nums">
              {meta.sources?.length ?? 0} sources
            </span>
            <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
            <span className="shrink-0 font-mono tabular-nums">
              {meta.itemCount ?? 0} headlines
            </span>
            <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
            <span className="shrink-0">latest {fmtRelative(item.occurredAt)}</span>
          </p>
        </div>
      </Link>
    </li>
  );
}

function renderExpiringBoardItem(item: InboxItem): JSX.Element {
  return (
    <li key={item.id}>
      <Link
        to={item.href ?? '#'}
        className="group flex items-start gap-3 px-5 py-3 hover:bg-surface-tinted"
      >
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
            {item.title}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-ink-subtle">
            {item.subtitle ? <span className="truncate">{item.subtitle}</span> : null}
            <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
            <span className="shrink-0 font-medium text-warning">
              expires {fmtRelative(item.occurredAt)}
            </span>
          </p>
        </div>
      </Link>
    </li>
  );
}

function renderFlaggedItem(item: InboxItem): JSX.Element {
  return (
    <li key={item.id}>
      <Link
        to={item.href ?? '#'}
        className="group flex items-start gap-3 px-5 py-3 hover:bg-surface-tinted"
      >
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-[13.5px] leading-snug text-ink group-hover:text-brand">
            {item.title}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-ink-subtle">
            {item.subtitle ? <span className="truncate">{item.subtitle}</span> : null}
            <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
            <span className="shrink-0">{fmtRelative(item.occurredAt)}</span>
          </p>
        </div>
      </Link>
    </li>
  );
}

